import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  requestIdleCallback,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { refreshCartPageAfterMutationWithFallback } from '@theme/cart-section-refresh';
import { fetchCartJson, seedCartCache } from '@theme/cart-api';
/* bundlechangesontjctheme: increasing a bundled drawer line adds the extra units as their own line. */
import { addBundleExtra } from '@theme/matching-products-bundle-cart';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { shouldDeferCartSideEffects } from '@theme/cart-add-perf';
import {
  computeBpExposureCents,
  DEFAULT_BP_GUEST_LIMIT,
  isBudgetPayCartLine,
  validateEligibility,
} from '@theme/budget-pay-rules';
import { alignCartBpTierFromCart } from '@theme/budget-pay-cart-align';
import { syncCartTokenAttribute } from '@theme/cart-token-sync';
import {
  isCartLineQuantityAllowed,
  getQuantityMaxErrorMessage,
  getResolvedLineMax,
} from '@theme/cart-line-quantity-limit';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_UPDATE_PAGE_LOADER_ID = 'cart-update-page-loader';
const TJC_GLOBAL_LOADER_ID = 'tjc-global-loader';

/** @type {number} */
let cartUpdatePageLoaderDepth = 0;

/** @returns {boolean} */
function useTjcPercentLoader() {
  return (
    Boolean(document.getElementById(TJC_GLOBAL_LOADER_ID)) &&
    typeof window.showTjcLoader === 'function' &&
    typeof window.hideTjcLoader === 'function'
  );
}

function setCartUpdatePageLoaderProgress(percent) {
  if (useTjcPercentLoader() && typeof window.setTjcLoaderProgress === 'function') {
    window.setTjcLoaderProgress(percent);
  }
}

function showCartUpdatePageLoader() {
  if (useTjcPercentLoader()) {
    window.showTjcLoader();
    return;
  }

  const el = document.getElementById(CART_UPDATE_PAGE_LOADER_ID);
  if (!el) return;

  cartUpdatePageLoaderDepth += 1;
  el.hidden = false;
  el.classList.remove('cart-update-page-loader-hidden');
}

function hideCartUpdatePageLoader() {
  if (useTjcPercentLoader()) {
    window.hideTjcLoader();
    return;
  }

  if (cartUpdatePageLoaderDepth === 0) return;

  cartUpdatePageLoaderDepth -= 1;
  if (cartUpdatePageLoaderDepth > 0) return;

  const el = document.getElementById(CART_UPDATE_PAGE_LOADER_ID);
  if (!el) return;
  el.hidden = true;
  el.classList.add('cart-update-page-loader-hidden');
}

/** Clear stuck overlay when the page is restored via back/forward (bfcache). */
function resetCartUpdatePageLoader() {
  cartUpdatePageLoaderDepth = 0;
  const el = document.getElementById(CART_UPDATE_PAGE_LOADER_ID);
  if (!el) return;
  el.hidden = true;
  el.classList.add('cart-update-page-loader-hidden');
}

window.addEventListener('pageshow', () => {
  resetCartUpdatePageLoader();
});

function parseBooleanOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return defaultValue;
}

function parseNumberOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

function getBpEligibilityFromBody() {
  const d = document.body?.dataset ?? {};
  const guestLimit = parseNumberOrDefault(d.bpGuestLimit, DEFAULT_BP_GUEST_LIMIT);
  const available = parseBooleanOrDefault(d.bpAvailable, true);
  const creditLimit = parseNumberOrDefault(d.bpCreditLimit, guestLimit);
  return { available, creditLimit };
}

function isBpLineItem(item) {
  return isBudgetPayCartLine(item);
}

/** @returns {string} */
function getCartChangeUrl() {
  const url = Theme.routes.cart_change_url ?? '/cart/change';
  return url.endsWith('.js') ? url : `${url}.js`;
}

/** @param {CartItemsComponent} component */
function isCartDrawerContext(component) {
  return component.closest('cart-drawer-component') != null;
}

/** @param {object} item */
function isFreeGiftCartLine(item) {
  const finalPrice = Number(item.final_price ?? 0);
  const originalPrice = Number(item.original_price ?? item.price ?? 0);
  if (finalPrice === 0 || originalPrice === 0) return true;

  const props = item.properties ?? {};
  const freeGiftProp = props._free_gift ?? props['free_gift'] ?? props['Free Gift'];
  return freeGiftProp != null && String(freeGiftProp).trim() !== '';
}

/**
 * Build GA4 product payload from a cart line row (before morph removes the row).
 * @param {HTMLElement | undefined} lineRow
 * @param {object | undefined} cartItem
 * @returns {object | undefined}
 */
function buildCartLineProductData(lineRow, cartItem) {
  if (!(lineRow instanceof HTMLElement)) return undefined;

  const priceCents = cartItem?.final_price ?? cartItem?.price;
  const price = priceCents != null ? Number(priceCents) / 100 : 0;

  return {
    masterProductId: lineRow.dataset.productId || '',
    item_id: lineRow.dataset.variantSku || lineRow.dataset.variantId || '',
    ams_item_id: lineRow.dataset.amsItemId || lineRow.dataset.variantId || lineRow.dataset.productId || '',
    variant_id: lineRow.dataset.variantId || '',
    item_name: lineRow.dataset.productTitle || '',
    price,
  };
}

/** @param {object | undefined} cart */
function countFreeGiftCartLines(cart) {
  return (cart?.items ?? []).filter(isFreeGiftCartLine).length;
}

/** @param {number} count */
function updateCartBubbleCount(count) {
  document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
    el.textContent = count < 100 ? String(count) : '';
    el.classList.toggle('hidden', count === 0);
  });
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', count === 0);
    bubble.classList.remove('hidden');
  });
  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
  });
  try {
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
  } catch (_) {
    // no-op
  }
}

/**
 * Ask BOGOS embed to evaluate/add gift lines after theme cart API changes.
 * @see https://bogos-api-integration.gitbook.io/bogos-api-integration/bogos-js-sdk
 * @param {object | undefined} cart
 */
function requestBogosGiftEvaluation(cart) {
  if (typeof document === 'undefined') return;

  /** @param {object} cartDetail */
  const dispatch = (cartDetail) => {
    try {
      document.dispatchEvent(new CustomEvent('bogos:gifts', { detail: cartDetail }));
    } catch (_) {
      // no-op
    }
  };

  if (cart && (Array.isArray(cart.items) || cart.token || cart.id)) {
    dispatch(cart);
    return;
  }

  fetchCartJson()
    .then((freshCart) => {
      if (freshCart) dispatch(freshCart);
    })
    .catch(() => {});
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Set<HTMLElement>} */
  #pendingRemovalRows = new Set();
  /** @type {number} */
  #freeGiftSyncToken = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('cart:buy-all-synced', this.#handleBuyAllSynced);
    document.addEventListener('cart:refresh', this.#handleExternalCartRefresh);
    document.addEventListener('cart:updated', this.#handleExternalCartUpdated);
    document.addEventListener('fg-gifts:updated', this.#handleBogosGiftUpdated);
    document.addEventListener('fg-cart:auto-updated', this.#handleBogosGiftUpdated);

    fetchCartJson()
      .then((cart) => {
        if (!cart) return;

        void syncCartTokenAttribute(cart).catch((err) =>
          console.error('[Cart] cart_token sync on connect', err),
        );

        window.RA?.cleanupExpiredAuctionCartLines?.({ cart, sourceId: this.sectionId })?.catch((err) =>
          console.error('[RA] Cart expiry cleanup failed', err),
        );
      })
      .catch((err) => console.error(err));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('cart:buy-all-synced', this.#handleBuyAllSynced);
    document.removeEventListener('cart:refresh', this.#handleExternalCartRefresh);
    document.removeEventListener('cart:updated', this.#handleExternalCartUpdated);
    document.removeEventListener('fg-gifts:updated', this.#handleBogosGiftUpdated);
    document.removeEventListener('fg-cart:auto-updated', this.#handleBogosGiftUpdated);
  }

  /**
   * BOGOS free-gift app embed may add/remove gift lines after cart mutations.
   * One delayed poll only when DOM and cart JSON may diverge.
   * @param {object | undefined} cartHint
   */
  #scheduleFreeGiftSync(cartHint) {
    const token = ++this.#freeGiftSyncToken;

    if (cartHint && this.#isCartDomInSyncWith(cartHint)) {
      const giftInCart = countFreeGiftCartLines(cartHint) > 0;
      const giftInDom = this.querySelectorAll('.cart-items__table-row--free-gift').length > 0;
      if (!giftInCart && !giftInDom) return;
    }

    const delaysMs = [800];

    const run = async () => {
      for (const delayMs of delaysMs) {
        if (token !== this.#freeGiftSyncToken) return;
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        if (token !== this.#freeGiftSyncToken) return;

        let cart;
        try {
          cart = await fetchCartJson();
        } catch {
          return;
        }

        const cartFreeGiftCount = countFreeGiftCartLines(cart);
        const domFreeGiftCount = this.querySelectorAll('.cart-items__table-row--free-gift').length;
        const cartItemCount = Number(cart.item_count ?? 0);
        const domItemCount = parseInt(
          this.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
          10
        );
        const domCount = Number.isFinite(domItemCount) ? domItemCount : null;

        const outOfSync =
          cartFreeGiftCount !== domFreeGiftCount ||
          (domCount != null && cartItemCount !== domCount);

        if (!outOfSync) return;

        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        this.#updateCartQuantitySelectorButtonStates();

        const refreshedCount = parseInt(
          this.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
          10
        );
        const itemCount = Number.isFinite(refreshedCount) ? refreshedCount : cartItemCount;
        updateCartBubbleCount(itemCount);

        document.dispatchEvent(
          new CartUpdateEvent(cart, this.sectionId, {
            action: 'update',
            itemCount,
            source: 'cart-items-component',
          })
        );
        return;
      }
    };

    run().catch((error) => {
      console.error('[Cart] Free gift sync failed', error);
    });
  }

  /** @param {object | undefined} cart */
  #isCartDomInSyncWith(cart) {
    const cartItemCount = Number(cart?.item_count ?? 0);
    const domItemCount = parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
    return Number.isFinite(domItemCount) && cartItemCount === domItemCount;
  }

  /** @param {object | undefined} cart */
  #shouldPollBogosAfterChange(cart) {
    if (countFreeGiftCartLines(cart) > 0) return true;
    if (this.querySelectorAll('.cart-items__table-row--free-gift').length > 0) return true;
    return false;
  }

  /** @param {Event} event */
  #handleExternalCartUpdated = (event) => {
    if (shouldDeferCartSideEffects(event)) return;
    if (event.detail?.skipFreeGiftSync) return;
    this.#scheduleFreeGiftSync();
  };

  /** Re-render cart lines after BOGOS adds/removes gift items. */
  #handleBogosGiftUpdated = () => {
    this.#scheduleFreeGiftSync();
  };

  /** Re-render cart section when deferred buy-all sync updated line properties. */
  #handleBuyAllSynced = () => {
    sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
  };

  /**
   * TWS-1397: the Live TV app embed (live-tv-cart-source-sync.js) rewrites or removes lines in the
   * background — e.g. a stale Missed Auction line that has since sold out — and announces it with a
   * payload-less `cart:refresh`. Re-render the lines and the bag count so the shopper is not left
   * looking at a line that is no longer in their cart.
   */
  #handleExternalCartRefresh = () => {
    sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
    fetchCartJson({ force: true })
      .then((cart) => {
        if (cart && typeof cart.item_count === 'number') updateCartBubbleCount(cart.item_count);
      })
      .catch(() => {});
  };

  /** Grouped bundle rows need the actual cart line, independent of visual order. */
  #getCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => Number(row.dataset.cartLine) === Number(line))
      ?? this.refs.cartItemRows[line - 1];
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const target = event.target;
    const ownsControl = target instanceof Node && this.contains(target);
    const documentFallback = target === document && this.classList.contains('tjc_cart_pg');
    // The mini-cart also listens on document. Handling the bag stepper there
    // starts a second /cart/change.js and a second loader, and the two writes
    // never both finish — the overlay stays at the 92% cap.
    if (!ownsControl && !documentFallback) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#cancelPendingRemovalRows();

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // Drawer: let morphSection refresh lines, header, meters, and subtotal together.
    // Optimistic row removal races morph and leaves totals/meters stale (IW cart page only).
    if (isCartDrawerContext(this)) return;

    const cartItemRowToRemove =
      this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        this.#pendingRemovalRows.delete(row);
        row.remove();
      };

      this.#pendingRemovalRows.add(row);

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /** @returns {void} */
  #cancelPendingRemovalRows() {
    for (const row of this.#pendingRemovalRows) {
      row.classList.remove('removing');
      row.style.removeProperty('--row-height');
    }
    this.#pendingRemovalRows.clear();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#freeGiftSyncToken += 1;
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const lineRow = this.#getCartItemRow(line);
    const isBpLine = lineRow?.dataset.sellingPlanLine === 'true';

    const previousQuantity = (() => {
      const fromDataset = parseInt(lineRow?.dataset.requestedQuantity ?? '', 10);
      if (Number.isFinite(fromDataset)) return fromDataset;
      const qtyInput = lineRow?.querySelector('input[data-cart-line]');
      if (qtyInput instanceof HTMLInputElement) {
        const fromInput = parseInt(qtyInput.value, 10);
        if (Number.isFinite(fromInput)) return fromInput;
      }
      return quantity;
    })();

    /** @type {object | undefined} */
    let targetItem;

    const needsBpIncreaseCheck = isBpLine && quantity > previousQuantity;

    if (needsBpIncreaseCheck) {
      try {
        const cart = await fetchCartJson();
        targetItem = cart?.items?.[line - 1];

        if (targetItem && isBpLineItem(targetItem)) {
          const { available, creditLimit } = getBpEligibilityFromBody();

          if (!available) {
            this.#handleCartError(line, {
              errors: 'Budget Pay is not available for your account.',
            });
            this.#enableCartItems();
            return;
          }

          const otherBpLines = (cart?.items ?? []).filter(
            (item, index) => isBpLineItem(item) && index !== line - 1,
          );
          const projectedLine = { ...targetItem, quantity };
          const exposure = computeBpExposureCents([...otherBpLines, projectedLine]);
          if (!exposure.ok) {
            this.#handleCartError(line, { errors: exposure.message });
            this.#enableCartItems();
            return;
          }
          const validation = validateEligibility(
            { available, creditLimit },
            exposure.exposureCents,
          );
          if (!validation.ok) {
            this.#handleCartError(line, { errors: validation.message });
            this.#enableCartItems();
            return;
          }
        }
      } catch (e) {
        console.error('[BudgetPay] Cart quantity precheck failed', e);
        this.#handleCartError(line, {
          errors: 'Could not validate Budget Pay quantity. Please refresh and try again.',
        });
        this.#enableCartItems();
        return;
      }
    }

    const qtyInput =
      lineRow?.querySelector('cart-quantity-selector-component input[ref="quantityInput"]') ??
      lineRow?.querySelector('cart-quantity-selector-component input[data-cart-quantity]');
    const qtyDropdown = lineRow?.querySelector('.cart-page__item-quantity-dropdown');
    const lineMaxFromDom =
      qtyInput instanceof HTMLInputElement && qtyInput.max
        ? parseInt(qtyInput.max, 10)
        : qtyDropdown instanceof HTMLElement && qtyDropdown.dataset.maxQty
          ? parseInt(qtyDropdown.dataset.maxQty, 10)
          : null;
    const lineMaxForCheck =
      Number.isFinite(lineMaxFromDom) && lineMaxFromDom > 0 ? lineMaxFromDom : null;

    if (!isCartLineQuantityAllowed(quantity, lineMaxForCheck)) {
      const maxForMessage = getResolvedLineMax(lineMaxForCheck);
      if (maxForMessage != null) {
        this.#handleCartError(line, {
          errors: getQuantityMaxErrorMessage(maxForMessage),
        });
        this.#enableCartItems();
        return;
      }
    }

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    showCartUpdatePageLoader();
    setCartUpdatePageLoaderProgress(12);

    /* bundlechangesontjctheme: only drawer rows marked cart-items__table-row--bundle take this path. */
    const isBundleIncrease = lineRow?.classList.contains('cart-items__table-row--bundle') && quantity > previousQuantity;
    const mutation = isBundleIncrease
      ? addBundleExtra({
          key: lineRow.dataset.key,
          previousQuantity,
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sectionsUrl: window.location.pathname,
          root: window.Shopify?.routes?.root ?? '/',
        }).then(({ cart, item }) => {
          targetItem = item;
          return JSON.stringify(cart);
        })
      : fetch(getCartChangeUrl(), fetchConfig('json', { body })).then((response) => response.text());

    mutation
      .then((responseText) => {
        setCartUpdatePageLoaderProgress(28);
        return responseText;
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        setCartUpdatePageLoaderProgress(45);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        let buyAllMutated = false;
        let sectionsPayload = parsedResponseText.sections ?? {};
        let sectionHtmlForMorph = sectionsPayload[this.sectionId];

        if (window.syncBuyAllPropertiesAfterCartChange) {
          buyAllMutated = await window.syncBuyAllPropertiesAfterCartChange();
          if (buyAllMutated) {
            sectionHtmlForMorph = await sectionRenderer.renderSection(this.sectionId, { cache: false });
            sectionsPayload = { ...sectionsPayload, [this.sectionId]: sectionHtmlForMorph };
          }
        }

        this.#cancelPendingRemovalRows();
        seedCartCache(parsedResponseText);

        let finalCart = parsedResponseText;

        const hadBpLine =
          isBpLine || (parsedResponseText.items ?? []).some((item) => isBpLineItem(item));

        if (hadBpLine) {
          try {
            setCartUpdatePageLoaderProgress(52);
            const { cart: alignedCart, didMutate: bpTierChanged } =
              await alignCartBpTierFromCart(parsedResponseText);
            finalCart = alignedCart;
            if (!bpTierChanged && parsedResponseText.sections) {
              finalCart = { ...alignedCart, sections: parsedResponseText.sections };
            }
          } catch (alignErr) {
            console.error('[BudgetPay] cart tier align after update', alignErr);
          }
        }

        const normalizedAction = quantity === 0 ? 'remove' : 'update';
        const delta = quantity - previousQuantity;
        const updatedCartItem = finalCart.items?.[line - 1];
        const productData = buildCartLineProductData(lineRow, targetItem ?? updatedCartItem);
        const trackingQuantity =
          normalizedAction === 'remove'
            ? previousQuantity
            : Math.abs(delta) || quantity;
        const skipBogosPoll = !this.#shouldPollBogosAfterChange(finalCart);
        const isCartPage = this.classList.contains('tjc_cart_pg');

        let newCartItemCount = Number(finalCart.item_count ?? 0);

        if (isCartPage) {
          let cartResponseForPage = finalCart;

          if (buyAllMutated && sectionHtmlForMorph) {
            cartResponseForPage = {
              ...finalCart,
              sections: {
                ...(finalCart.sections ?? parsedResponseText.sections ?? {}),
                [this.sectionId]: sectionHtmlForMorph,
              },
            };
          } else if (!finalCart.sections && parsedResponseText.sections) {
            cartResponseForPage = { ...finalCart, sections: parsedResponseText.sections };
          }

          setCartUpdatePageLoaderProgress(62);
          await refreshCartPageAfterMutationWithFallback(cartResponseForPage, {
            action: normalizedAction,
            source: 'cart-items-component',
            skipFreeGiftSync: skipBogosPoll,
            eventData: {
              line,
              quantity,
              previousQuantity,
              delta,
              productData: productData
                ? { ...productData, quantity: trackingQuantity }
                : undefined,
            },
          });
          const refreshedCount = parseInt(
            this.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
            10,
          );
          if (Number.isFinite(refreshedCount)) {
            newCartItemCount = refreshedCount;
          }
        } else if (!buyAllMutated) {
          setCartUpdatePageLoaderProgress(62);
          if (sectionHtmlForMorph) {
            await morphSection(this.sectionId, sectionHtmlForMorph);
          } else {
            sectionHtmlForMorph = await sectionRenderer.renderSection(this.sectionId, { cache: false });
            sectionsPayload = { ...sectionsPayload, [this.sectionId]: sectionHtmlForMorph };
          }

          setCartUpdatePageLoaderProgress(78);

          const newSectionHTML = sectionHtmlForMorph
            ? new DOMParser().parseFromString(sectionHtmlForMorph, 'text/html')
            : null;

          const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
          const parsedHiddenCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : NaN;
          newCartItemCount = Number.isFinite(parsedHiddenCount)
            ? parsedHiddenCount
            : Number(finalCart.item_count ?? 0);

          document.dispatchEvent(
            new CartUpdateEvent(finalCart, this.sectionId, {
              action: normalizedAction,
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: sectionsPayload,
              line,
              quantity,
              previousQuantity,
              delta,
              productData: productData
                ? { ...productData, quantity: trackingQuantity }
                : undefined,
            }),
          );

          updateCartBubbleCount(newCartItemCount);
        }

        this.#updateQuantitySelectors(finalCart);
        this.#updateCartQuantitySelectorButtonStates();

        if (quantity > 0 && !isBundleIncrease) {
          const updatedRow = this.#getCartItemRow(line);
          if (updatedRow instanceof HTMLElement) {
            updatedRow.dataset.requestedQuantity = String(quantity);
            const pageError = updatedRow.querySelector('.cart-page__item-bp-error');
            if (pageError instanceof HTMLElement) {
              pageError.hidden = true;
              pageError.classList.add('hidden');
            }
          }
        }

        requestBogosGiftEvaluation(finalCart);
        if (!skipBogosPoll) {
          this.#scheduleFreeGiftSync(finalCart);
        }
      })
      .catch((error) => {
        console.error(error);
        this.#handleCartError(line, { errors: error.message || 'Could not update your bag. Please try again.' });
      })
      .finally(() => {
        hideCartUpdatePageLoader();
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const errorText =
      typeof parsedResponseText.errors === 'string'
        ? parsedResponseText.errors
        : JSON.stringify(parsedResponseText.errors ?? '');

    const row = this.#getCartItemRow(line);
    const previousQty =
      row instanceof HTMLElement && row.dataset.requestedQuantity
        ? parseInt(row.dataset.requestedQuantity, 10)
        : NaN;

    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const drawerQtyInput = quantitySelector?.querySelector('input');
    if (drawerQtyInput instanceof HTMLInputElement) {
      drawerQtyInput.value = drawerQtyInput.defaultValue;
    }

    const miniCartQtyInput = this.querySelector(
      `cart-quantity-selector-component input[data-cart-line="${line}"]`,
    );
    if (miniCartQtyInput instanceof HTMLInputElement) {
      miniCartQtyInput.value = miniCartQtyInput.defaultValue;
    }

    if (Number.isFinite(previousQty)) {
      const hidden = this.querySelector(
        `.cart-page__item-quantity-hidden[data-line-index="${line}"]`,
      );
      if (hidden instanceof HTMLInputElement) {
        hidden.value = String(previousQty);
      }
      window.syncCartPageQuantityUi?.(line, previousQty);
      window.paintCartLineExtendedPrice?.(line, previousQty);
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = errorText;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      const pageError = row?.querySelector('.cart-page__item-bp-error');
      const pageErrorText = row?.querySelector('.cart-page__item-bp-error-text');
      if (pageError instanceof HTMLElement && pageErrorText instanceof HTMLElement) {
        pageErrorText.textContent = errorText;
        pageError.hidden = false;
        pageError.classList.remove('hidden');
      } else {
        console.error('[BudgetPay] Cart item error not found', { line, errorText });
      }
    }

    const qtyInput = this.querySelector(
      `cart-quantity-selector-component input[data-cart-line="${line}"]`,
    );
    const requestedQty =
      qtyInput instanceof HTMLInputElement ? parseInt(qtyInput.value, 10) : undefined;

    window.gtmTrack?.trackCartError?.({
      error_message: errorText,
      element: row instanceof HTMLElement ? row : cartItemErrorContainer,
      requested_quantity: Number.isFinite(requestedQty) ? requestedQty : undefined,
    });
  };

  /**
   * Theme setting "Load theme scripts when first needed" on: a cart:update that happened before
   * this file loaded (the cart group in snippets/scripts.liquid) is handed here once the drawer is
   * defined, so the lines update as they would have. Only this element's handler sees it again.
   * This element was defined moments before, and Component starts watching its subtree for refs
   * only when the browser is next idle, so the lines this update morphs in would be missing from
   * refs (a remove tap in the drawer then finds no line). refs are read again right after the
   * handler, and once more at that idle time for a section render that lands before it.
   * @param {CartUpdateEvent} event
   */
  catchUpCartUpdate(event) {
    this.#handleCartUpdate(event);
    this.updatedCallback();
    requestIdleCallback(() => this.updatedCallback());
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (shouldDeferCartSideEffects(event)) return;

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const source = event.detail?.data?.source;

    // Section already morphed by cart-section-refresh (membership, promo, BP sync, etc.)
    if (
      source === 'cart-items-component' &&
      event.detail?.sourceId === this.sectionId
    ) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    if (
      source === 'tjc-membership' ||
      source === 'tjc-promo' ||
      source === 'main-cart-bp-sync' ||
      source === 'cart-section-refresh' ||
      source === 'cart-items-component-bp' ||
      source === 'cart-page-budget-pay-choice'
    ) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
